import type { Card } from "./card";
import type { DeckConfig } from "../engine/deck";
import { normalizeHouseRules, type HouseRules, type StreakLock, type StreakTarget } from "../engine/houseRules";
import type { EngineEvent, GamePhase, StackIndex, StarfallBounty } from "./game";
import type { LobbyMode, TeamId } from "../engine/teams";

/**
 * The wire/Firestore contract for online games. This is the ONLY shape ever
 * sent to clients when playing online - it deliberately omits deck contents
 * and stack contents beyond the visible top card, per the design doc's
 * "all deck orders are authoritative on the server and are never sent to
 * clients" rule. The Go game server (services/game-server) produces JSON
 * matching this shape; keep the two in sync if either changes.
 *
 * The one exception is `PublicPlayerView.peekCard`: a player whose Peek Card
 * fired has their next card shown face up to the whole table, which is the
 * rule, not a leak. It is exactly one card, only while the
 * peek is active, and it never says anything about the order of the rest.
 */
export interface PublicStackView {
  count: number;
  topCard: Card | null;
}

export interface PublicStarfallView {
  stars: number;
  /** Stars that fill the meter, so the client draws the sockets the server counts to. */
  meterSize: number;
  bounty: StarfallBounty | null;
}

export interface PublicPlayerView {
  id: string;
  nickname: string;
  isHost: boolean;
  connected: boolean;
  deckCount: number;
  /**
   * Turns in a row without taking a pile. Public because the fire on a deck is,
   * and because everyone at the table needs to see a streak coming — it is the
   * only warning that two piles are about to shut.
   */
  streak: number;
  /**
   * Piles taken on consecutive turns (Drought). Public because the mist on a
   * deck is, and the table should see a catch-up coming as it sees a streak.
   * Absent on documents written before Drought; read as 0.
   */
  drought?: number;
  /**
   * The top card of this player's deck while a Peek they fired is active, else
   * null — the only deck card ever published (see `visiblePeekCard`, which is
   * what decides it).
   *
   * Every client draws it: the Peek Card shows the next card to the whole
   * table, which is the pressure the card exists to create.
   */
  peekCard: Card | null;
  /**
   * A party player's raised hand, set and lowered only by its owner
   * (`POST /games/{id}/hand`). Only ever present on a party parent's roster;
   * absent everywhere else, which reads as down.
   */
  handRaised?: boolean;
  /**
   * The 1-based table the host put this player at, in a custom layout
   * (`POST /games/{id}/tables`). Lobby/parent document only; absent or 0 means
   * unassigned, which `normalizeTables` seats at the emptiest table.
   */
  table?: number;
  /**
   * The id of the team this player is on (`TEAM_IDS`, `POST /games/{id}/teams`).
   * Kept while the lobby is in free-for-all so switching back restores the
   * teams. On the lobby/parent roster AND on a dealt table's players, so the
   * table can color nameplates; absent for observers and when there are no teams.
   */
  team?: TeamId;
  /**
   * The gameId of the party table this player is looking at instead of their
   * own (`POST /games/{id}/viewing`). Parent roster only; absent (or null) means
   * at their own table. The server clears it at every deal, and at the fan-in
   * for the finishing table's players.
   */
  viewing?: string | null;
}

/**
 * Somebody the host has removed AND blocked from this game's join code.
 *
 * Published so the host's lobby can list them and offer a way back — a ban with
 * no undo turns one mis-tap into "end the game and re-host". It reveals nothing
 * new: both fields belonged to somebody who was at this table, and every client
 * already sees the ids and nicknames of everyone in the game.
 */
export interface PublicBannedPlayerView {
  playerId: string;
  nickname: string;
  bannedAtMs: number;
}

/**
 * One player's level crest as the server publishes it: their level, a frame,
 * and three badge sockets (left, right, bottom; null is an empty socket).
 *
 * Typed loosely on purpose. The ids and rims are the web app's catalog
 * (`theme/crestCatalog.json`), which this package does not know, and a crest is
 * network data that the app puts through `sanitizeCrest` before drawing it.
 */
export interface PublicCrest {
  level: number;
  frame: string;
  badges: ({ id: string; rim: string } | null)[];
}

export interface PublicTurnTimer {
  /** Configured turn timer length, or null when the host didn't set one. */
  seconds: number | null;
  /**
   * Server epoch-ms deadline for the current turn.
   *
   * It includes the time the client will spend animating the move that just
   * resolved, so it is further out than `seconds` — a player's turn is measured
   * from when they can actually act, not from when the server wrote the move.
   * The countdown is therefore `min(deadlineMs - now, seconds)`.
   * See docs/TURN_TIMER.md.
   */
  deadlineMs: number | null;
  /**
   * True when this deadline only exists to auto-play for a disconnected or idle
   * player, rather than being a turn timer the host configured. No countdown is
   * rendered for it.
   *
   * Clients do not report either kind any more: the server schedules its own
   * enforcement when it sets the deadline. Every client polling this once a
   * second is what wrote both game documents on every tick and deadlocked the
   * table it was trying to un-stall.
   */
  graceOnly: boolean;
}

export interface PublicPendingBonusChoice {
  type: "match-off-by-one-bonus";
  playerId: string;
  originStackIndex: StackIndex;
  remainingPlacements: number;
}

/**
 * Go publishes one flat struct for both decisions, so a swap decision arrives
 * carrying an `originStackIndex` of 0 as well. Ignore it: it means nothing here,
 * and 0 is a real pile.
 */
export interface PublicPendingSwapChoice {
  type: "swap-top-cards";
  playerId: string;
  remainingSwaps: number;
}

export type PublicPendingDecision = PublicPendingBonusChoice | PublicPendingSwapChoice;

/** The running match record, mirrored from the server's MatchState. */
export interface PublicMatchView {
  matchId: string;
  roundNumber: number;
  totals: Record<string, number>;
  rounds: {
    roundNumber: number;
    scores: Record<string, number>;
    cardsLeft: Record<string, number>;
    winnerPlayerId: string | null;
    /** One entry per table that finished; absent on rounds banked before parties recorded them. */
    winnerPlayerIds?: string[];
    finishedAtMs: number;
  }[];
  /** Names for ids on the board, including players who have since left. Absent on older documents. */
  nicknames?: Record<string, string>;
}

/**
 * One table within a party, as published on the parent document's directory.
 * Per-instance scores and cards-left stay private on the parent — they are
 * already public on the instance's own document.
 */
export interface PublicPartyInstanceView {
  gameId: string;
  /** Seat order within the instance. */
  playerIds: string[];
  status: "playing" | "round-over";
  /** Set once this instance's round is over. */
  winnerPlayerId: string | null;
}

export interface PublicPartyView {
  instances: PublicPartyInstanceView[];
  /**
   * The turn count the party's round has been fixed at by the first player out
   * anywhere in it, or 0 before anybody has gone out.
   *
   * Published so a client watching only the parent can say the party is in its
   * final turns without subscribing to every table to work it out.
   */
  lastLapTurns?: number;
}

export interface PublicGameState {
  id: string;
  code: string;
  phase: GamePhase;
  /** The deck the host configured for this table, so joiners see it before the deal. */
  deckConfig: DeckConfig;
  /**
   * The optional rules the host switched on, so joiners see them before the deal
   * and every client can judge input legality the same way the server does.
   */
  houseRules: HouseRules;
  /**
   * The card deck and table the host had selected, so every client renders the
   * host's table — including a deck or venue the joiner has not unlocked. Absent
   * on a game created before this existed, which a client reads as "use my own
   * selection". Cosmetic only; the server never interprets them.
   */
  hostThemeId?: string;
  hostVenueId?: string;
  /** How many people may join a free host's table. Absent means no limit beyond the party's. */
  joinerLimit?: number;
  /** The host deleted this table, so whoever is still watching it should leave. */
  closedByHost?: boolean;
  /**
   * Each player's level crest, by player id, as they brought it to the table -
   * checked against the cloud save for a Google sign-in, shape-checked only for
   * a guest. Absent when nobody brought one, and on games from before crests;
   * a player with no entry draws no crest. Cosmetic only.
   */
  crests?: Record<string, PublicCrest>;
  currentPlayerId: string | null;
  stacks: [PublicStackView, PublicStackView, PublicStackView, PublicStackView, PublicStackView];
  players: PublicPlayerView[];
  /**
   * People who joined while a round was in progress. They hold no cards and
   * take no turns — they watch the round out, and the next deal seats them.
   *
   * Kept out of `players` rather than flagged inside it because every rule in
   * both engines reads the roster as "everyone holding cards", and a seat with
   * an empty deck reads as a player who has gone out. `deckCount` and `streak`
   * are published as the zeroes they are.
   */
  pendingPlayers: PublicPlayerView[];
  /**
   * Members watching instead of playing ("Just watch"). Like `pendingPlayers`
   * they hold no cards, but no deal seats them: they stay watchers until they
   * take a seat themselves. On the lobby's document only. The host can be one.
   */
  observers: PublicPlayerView[];
  turnTimer: PublicTurnTimer | null;
  /** Piles shut for the player to act, by a streak somebody spent on them. */
  streakLocks: StreakLock[];
  /**
   * Who a spent streak has marked, and how many piles will shut when their turn
   * comes round. Public because it is drawn on the marked player's deck — it is
   * the only warning anyone gets, and the whole table has to be able to read it.
   */
  streakTargets: StreakTarget[];
  /**
   * Starfall's public half: the meter and the Bounty Pile. Null when the rule is
   * not in force this round (off, a half deck, or a document written before it).
   * The event deck and its generator stay on the server - they decide what
   * happens next, exactly as deck order does.
   */
  starfall: PublicStarfallView | null;
  pendingDecision: PublicPendingDecision | null;
  winnerPlayerId: string | null;
  /** playerId -> position points once the round is over. */
  scores: Record<string, number> | null;
  /** playerId -> cards left in hand, shown next to the points. */
  cardsLeft: Record<string, number> | null;
  roundNumber: number;
  match: PublicMatchView | null;
  /**
   * The host cleared the leaderboard between rounds, so the finished-but-unbanked
   * round in `scores` must NOT be re-folded into the fresh match on screen
   * (`state/onlineMatch.ts`). Absent on most documents, and on any written before
   * this existed, which reads as false. See the Go GameState.MatchCleared.
   */
  matchCleared?: boolean;
  /**
   * Epoch ms of the host's latest "Show match podium" (`POST /games/{id}/podium`).
   * Strictly increasing and never reset, so a client plays the podium once per
   * value. Lobby/parent document only; absent until the host first asks.
   */
  matchPodiumAtMs?: number;
  version: number;
  /** Events from the most recently-applied move, for client-side reveal animation. Never contains stack-taken.revealedCards. */
  lastEvents: EngineEvent[];
  /**
   * Sequence number of the last event in `lastEvents`. Clients track the highest
   * seq they've played and ignore anything at or below it, so a re-delivered or
   * out-of-order Firestore snapshot can't replay animations.
   */
  eventSeq: number;
  /**
   * The instance directory, on a party's PARENT document only. Absent or null
   * everywhere else, including on a lobby of 8 or fewer that never becomes a
   * party. See docs/PARTY_MODE.md.
   */
  party?: PublicPartyView | null;
  /**
   * How many tables the host has laid out by hand. Absent or 0 is the AUTO layout
   * (the split derived from the roster); above 0 is a CUSTOM layout of that many
   * tables, each player's own at `PublicPlayerView.table`. Lobby/parent only.
   */
  tableCount?: number;
  /**
   * `"teams"` when the host has the lobby in Teams mode; absent means
   * free-for-all. Published on the lobby/parent AND on dealt tables.
   */
  mode?: LobbyMode;
  /**
   * The teams in play, in display order (ids from `TEAM_IDS`). Absent until the
   * host first switches to Teams, then kept even back in free-for-all. Published
   * on the lobby/parent AND on dealt tables.
   */
  teams?: TeamId[];
  /**
   * The parent's game id, on a party's CHILD documents only. A non-null partyId
   * is the "this document is an instance" test — never `instanceIndex`, since 0
   * is a valid index.
   */
  partyId?: string | null;
  /** This instance's position in the parent's directory. Children only. */
  instanceIndex?: number | null;
  /**
   * The turn count THIS table's round has been fixed at, on instance documents.
   *
   * Published because a table can now be in its last lap with nobody sitting at
   * it out — the player who fixed the round was at another table — and a round
   * that ends with no explanation on screen reads as the game having broken.
   */
  partyLastLapTurns?: number | null;
  /**
   * Who the host has blocked from this join code, on the LOBBY's document only —
   * in a party that is the parent, which is what the code resolves to. Absent on
   * almost every game, since almost no game has anybody blocked.
   */
  bannedPlayers?: PublicBannedPlayerView[] | null;
}

/** Strips an EngineEvent list down to what's safe to broadcast to every online client. */
export function sanitizeEventsForBroadcast(events: EngineEvent[]): EngineEvent[] {
  return events.map((e) => (e.type === "stack-taken" ? { ...e, revealedCards: undefined } : e));
}

/**
 * Repairs a raw Firestore document into a PublicGameState the UI can trust.
 *
 * This exists because the document is produced by the Go server, and Go's `nil`
 * slices and maps cross the wire as `null` rather than `[]` / `{}`. TypeScript
 * types the fields as non-nullable, so the compiler happily lets `state.match.rounds.length`
 * through and it explodes at runtime on a freshly created lobby. The Go side now
 * emits empty collections, but documents written by an older build still exist
 * (games live 30 minutes, deploys are not atomic), and this is the one place all
 * remote data enters the app — so it gets normalized here rather than every
 * caller having to remember to guard.
 *
 * Anything genuinely optional (scores, winner, pendingDecision, turnTimer) stays
 * nullable; only the "should always be a collection" fields are filled in.
 */
export function normalizePublicGameState(raw: PublicGameState): PublicGameState {
  const match = raw.match
    ? {
        ...raw.match,
        totals: raw.match.totals ?? {},
        rounds: (raw.match.rounds ?? []).map((r) => ({
          ...r,
          scores: r.scores ?? {},
          cardsLeft: r.cardsLeft ?? {},
        })),
      }
    : null;

  // The table always has exactly five piles, and the renderer indexes all five.
  // Pad rather than pass a short array through, so a malformed document degrades
  // to an empty table instead of throwing mid-render.
  const rawStacks = raw.stacks ?? [];
  const stacks = Array.from({ length: 5 }, (_, i) => rawStacks[i] ?? { count: 0, topCard: null });

  // Party fields are absent on every non-party document, so "missing" is the
  // normal case rather than a malformed one; they settle to null. An unknown
  // status reads as "playing" so a stale or garbled instance entry can't send
  // the whole party to the combined leaderboard early.
  const party = raw.party
    ? {
        ...raw.party,
        instances: (raw.party.instances ?? []).map((i) => ({
          ...i,
          playerIds: i.playerIds ?? [],
          status: i.status === "round-over" ? ("round-over" as const) : ("playing" as const),
          winnerPlayerId: i.winnerPlayerId ?? null,
        })),
        lastLapTurns: raw.party.lastLapTurns ?? 0,
      }
    : null;

  return {
    ...raw,
    // A document written before house rules existed has no block at all, and
    // "rule absent" has to read as "rule off" rather than as undefined.
    houseRules: normalizeHouseRules(raw.houseRules),
    // Same reason as the collections below: a document written before streaks
    // existed carries no lock list, and every screen maps over it.
    streakLocks: raw.streakLocks ?? [],
    // Absent on every document written before streaks were aimed at a player,
    // and absent most of the time even now - nobody is marked on most turns.
    streakTargets: raw.streakTargets ?? [],
    starfall: raw.starfall ?? null,
    // `peekCard` is absent on documents written before Peek existed, and Go
    // publishes it as null whenever no peek is active — which is almost always.
    players: (raw.players ?? []).map((p) => ({ ...p, streak: p.streak ?? 0, drought: p.drought ?? 0, peekCard: p.peekCard ?? null })),
    // Absent on every document written before mid-round joining existed, and
    // absent is the common case even now — most games never have anyone
    // waiting. Every screen maps over it.
    pendingPlayers: (raw.pendingPlayers ?? []).map((p) => ({
      ...p,
      deckCount: p.deckCount ?? 0,
      streak: p.streak ?? 0,
      peekCard: p.peekCard ?? null,
    })),
    // Absent on every document written before observers existed.
    observers: (raw.observers ?? []).map((p) => ({
      ...p,
      deckCount: p.deckCount ?? 0,
      streak: p.streak ?? 0,
      peekCard: p.peekCard ?? null,
    })),
    stacks: stacks as PublicGameState["stacks"],
    lastEvents: raw.lastEvents ?? [],
    eventSeq: raw.eventSeq ?? 0,
    roundNumber: raw.roundNumber ?? 1,
    match,
    // Absent on almost every document, and on any written before the host could
    // clear the leaderboard between rounds; read as "don't skip the re-fold".
    matchCleared: raw.matchCleared ?? false,
    party,
    partyId: raw.partyId ?? null,
    instanceIndex: raw.instanceIndex ?? null,
    partyLastLapTurns: raw.partyLastLapTurns ?? 0,
    // Absent is the normal case, and every screen maps over it.
    bannedPlayers: raw.bannedPlayers ?? [],
  };
}
